'use client'

import Image from 'next/image'
import { cn } from 'src/utilities/cn'
import React from 'react'
import RichText from '@/components/RichText'
import type { MediaBlock as MediaBlockProps } from '@/payload-types'
import { getScreenStyle } from '@/blocks/BlockStyle'
import { useTailwindScreen } from '@/lib/client/hooks/useTailwindScreen'
import { resolveDoc } from '@/lib/server/resolveDoc'
import useDoc from '@/lib/client/useDoc'

type Props = MediaBlockProps

export const MediaBlock: React.FC<Props> = ({
  media,
  maxWidth,
  maxHeight,
  className,
  imgClassName,
  flat,
  style: responsiveStyle,
}) => {
  'use client'

  const screen = useTailwindScreen()
  const style = getScreenStyle(responsiveStyle ?? {}, screen ?? 'sm')
  const poster = useDoc('media', typeof media === 'string' ? null : media.video?.poster)

  if (!media || typeof media === 'string') return null

  let width = null as number | null | undefined
  let height = null as number | null | undefined

  if (style.width) {
    width = parseFloat(style.width)
    if (!height && media.width && media.height) {
      height = (width * media.height) / media.width
    }
  }
  if (style.height) {
    height = parseFloat(style.height)
    if (!width && media.width && media.height) {
      width = (height * media.width) / media.height
    }
  }
  const defaultWidth = 600
  const defaultHeight =
    media.width && media.height ? (media.height / media.width) * defaultWidth : 180

  if (!media.url) return null

  if (media.mimeType?.startsWith('image/')) {
    return (
      <div className={cn('flex flex-col items-center', className)}>
        <div
          className={cn(
            'flex place-items-centeroutline overflow-hidden',
            !flat && 'outline-1 outline-stone-200  rounded-2xl shadow',
          )}
        >
          <Image
            className={cn(
              !flat && 'scale-100 hover:scale-105 transition-all duration-1000 ease-in-out',
              imgClassName,
            )}
            alt={media.alt ?? ''}
            src={media.url}
            width={width ?? defaultWidth}
            height={height ?? defaultHeight}
            style={Object.fromEntries(
              Object.entries(style).filter(([key, value]) => value !== null),
            )}
          />
        </div>
        {media.caption && (
          <div className={cn('mt-1', 'text-xs text-center primary-links w-full')}>
            <RichText content={media.caption} enableProse={false} enableGutter={false} />
          </div>
        )}
      </div>
    )
  }

  if (media.mimeType?.startsWith('video/')) {
    return (
      <div className={cn('flex flex-col items-center', className)}>
        <video controls poster={poster?.url ? poster.url : undefined}>
          <source src={media.url} type={media.mimeType} />
          Your browser does not support the video tag.
        </video>
      </div>
    )
  }

  return null
}
